/**
 * Key Setup Wizard - Complete wizard for key generation and backup
 *
 * Guides users through:
 * 1. Entering their name
 * 2. Optional: Entering authority profile information (for authorities only)
 * 3. Key generation
 * 4. PDF backup download
 */

import { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Alert,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { KeyGenerationStep } from './KeyGenerationStep';
import { KeyBackupStep } from './KeyBackupStep';
import { ProfileStep, ProfileData } from './ProfileStep';
import { useCryptoKeyStore, StoredKeyPair, KeyOwnerType } from '@antragsmanager/crypto-lib';

export interface KeySetupWizardProps {
  /** The type of account being created */
  ownerType: KeyOwnerType;

  /** Callback when setup is complete */
  onComplete: (keyPair: StoredKeyPair, profileData?: ProfileData) => void;

  /** Callback to cancel setup */
  onCancel?: () => void;

  /** Title for the wizard */
  title?: string;

  /** Subtitle/description */
  subtitle?: string;

  /** Whether to show the profile step (only for authorities) */
  showProfileStep?: boolean;
}

type WizardStep = 'name' | 'profile' | 'generate' | 'backup';

const getSteps = (showProfile: boolean) =>
  showProfile
    ? ['Ihre Daten', 'Behörden-Info', 'Schlüssel erstellen', 'Backup sichern']
    : ['Ihre Daten', 'Schlüssel erstellen', 'Backup sichern'];

export function KeySetupWizard({
  ownerType,
  onComplete,
  onCancel,
  title = 'Konto erstellen',
  subtitle = 'Erstellen Sie Ihren persönlichen Zugangsschlüssel',
  showProfileStep = ownerType === 'AUTHORITY',
}: KeySetupWizardProps) {
  const [currentStep, setCurrentStep] = useState<WizardStep>('name');
  const [ownerName, setOwnerName] = useState('');
  const [profileData, setProfileData] = useState<ProfileData | undefined>(undefined);
  const [generatedKeyPair, setGeneratedKeyPair] = useState<StoredKeyPair | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);

  const steps = getSteps(showProfileStep);

  const {
    generateAndActivateKey,
    isLoading,
    error: storeError,
  } = useCryptoKeyStore();

  // Get step index for stepper
  const getStepIndex = (): number => {
    switch (currentStep) {
      case 'name':
        return 0;
      case 'profile':
        return 1;
      case 'generate':
        return showProfileStep ? 2 : 1;
      case 'backup':
        return showProfileStep ? 3 : 2;
      default:
        return 0;
    }
  };

  // Handle name submission
  const handleNameSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setNameError(null);

    const trimmedName = ownerName.trim();
    if (!trimmedName) {
      setNameError('Bitte geben Sie Ihren Namen ein.');
      return;
    }

    if (trimmedName.length < 2) {
      setNameError('Der Name muss mindestens 2 Zeichen lang sein.');
      return;
    }

    // Go to profile step for authorities, otherwise skip to generate
    setCurrentStep(showProfileStep ? 'profile' : 'generate');
  };

  // Handle profile submission
  const handleProfileSubmit = useCallback((profile: ProfileData) => {
    setProfileData(profile);
    setCurrentStep('generate');
  }, []);

  // Handle profile skip
  const handleProfileSkip = useCallback(() => {
    setProfileData(undefined);
    setCurrentStep('generate');
  }, []);

  // Generate key function to pass to generation step
  const generateKey = useCallback(async (): Promise<StoredKeyPair> => {
    const keyPair = await generateAndActivateKey({
      ownerType,
      ownerName: ownerName.trim(),
    });
    return keyPair;
  }, [generateAndActivateKey, ownerType, ownerName]);

  // Handle key generated
  const handleKeyGenerated = useCallback((keyPair: StoredKeyPair) => {
    setGeneratedKeyPair(keyPair);
    setCurrentStep('backup');
  }, []);

  // Handle backup confirmed
  const handleBackupConfirmed = useCallback(() => {
    if (generatedKeyPair) {
      onComplete(generatedKeyPair, profileData);
    }
  }, [generatedKeyPair, onComplete, profileData]);

  // Handle back button (reserved for future wizard navigation)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleBack = () => {
    if (currentStep === 'backup' && !generatedKeyPair) {
      setCurrentStep('generate');
    } else if (currentStep === 'generate') {
      setCurrentStep(showProfileStep ? 'profile' : 'name');
    } else if (currentStep === 'profile') {
      setCurrentStep('name');
    }
  };
  void handleBack; // Reserved for future UI enhancement

  return (
    <Card sx={{ maxWidth: 500, mx: 'auto', my: 4 }}>
      <CardContent sx={{ p: 4 }}>
        {/* Header */}
        <Typography variant="h5" component="h1" gutterBottom align="center">
          {title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ mb: 4 }}
        >
          {subtitle}
        </Typography>

        {/* Stepper */}
        <Stepper activeStep={getStepIndex()} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step content */}
        <Box>
          {/* Name step */}
          {currentStep === 'name' && (
            <Box component="form" onSubmit={handleNameSubmit}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <PersonIcon sx={{ fontSize: 48, color: 'primary.main' }} />
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Geben Sie Ihren Namen ein. Dieser wird in Ihrem Schlüssel-Backup
                gespeichert.
              </Typography>

              {nameError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {nameError}
                </Alert>
              )}

              <TextField
                fullWidth
                label="Ihr Name"
                value={ownerName}
                onChange={(e) => setOwnerName(e.target.value)}
                autoFocus
                required
                sx={{ mb: 3 }}
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                {onCancel && (
                  <Button variant="outlined" onClick={onCancel}>
                    Abbrechen
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  Weiter
                </Button>
              </Box>
            </Box>
          )}

          {/* Profile step (for authorities only) */}
          {currentStep === 'profile' && showProfileStep && (
            <ProfileStep
              onSubmit={handleProfileSubmit}
              onSkip={handleProfileSkip}
              initialData={profileData}
            />
          )}

          {/* Generation step */}
          {currentStep === 'generate' && (
            <KeyGenerationStep
              autoGenerate={true}
              onKeyGenerated={handleKeyGenerated}
              generateKey={generateKey}
              isGenerating={isLoading}
              error={storeError?.message}
              keyPair={generatedKeyPair}
            />
          )}

          {/* Backup step */}
          {currentStep === 'backup' && generatedKeyPair && (
            <KeyBackupStep
              keyPair={generatedKeyPair}
              ownerName={ownerName.trim()}
              ownerType={ownerType}
              onBackupConfirmed={handleBackupConfirmed}
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
